import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useT } from "@/i18n/i18n";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Field, Input, Textarea, Btn } from "@/pages/app/kit";
import { SectionHeading } from "@/components/shared";
import { Flower2, Bug, ShieldCheck, FlaskConical, Search } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1471943311424-646960669fbc?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function PolskaKwitnie() {
  const { lang } = useT();
  const L = (pl, en) => (lang === "pl" ? pl : en);
  const nav = useNavigate();
  const [form, setForm] = useState({ role: "beekeeper", name: "", organization: "", email: "", phone: "", region: "", message: "" });
  const [ref, setRef] = useState(null);
  const [batch, setBatch] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/leads", form); setRef(data.reference); toast.success(L("Zgłoszenie zapisane", "Submitted")); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const cards = [
    { icon: Flower2, t: L("Siedliska i pożytki", "Habitat & forage"), d: L("Kwietne miedze, żywopłoty, rośliny okrywowe, punkty wodne — działania na rzecz zapylaczy.", "Field margins, hedgerows, cover crops, water points — pollinator-support actions.") },
    { icon: Bug, t: L("Usługi zapylania", "Pollination services"), d: L("Łączymy rolników z pszczelarzami. Dopasowanie zatwierdzają obie strony.", "We connect farmers with beekeepers. Both parties approve the match.") },
    { icon: ShieldCheck, t: L("Alerty o zabiegach", "Treatment alerts"), d: L("Narzędzie komunikacji o planowanych zabiegach — nie porada prawna.", "A communication tool for planned treatments — not legal advice.") },
    { icon: FlaskConical, t: L("Jakość i pochodzenie", "Quality & origin"), d: L("Rejestracja partii, testy laboratoryjne, prawdziwe polskie pochodzenie.", "Batch registration, lab testing, truthful Polish origin.") },
  ];

  return (
    <div>
      <section className="relative isolate overflow-hidden bg-forestdark">
        <img src={HERO} alt="Flowering orchard and pollinators" className="absolute inset-0 h-full w-full object-cover" />
        <div className="zp-hero-overlay absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-4 py-24">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white" style={{ color: "#E1B84B" }}>Polska Kwitnie · Miód Żywiącej Polski</div>
          <h1 className="max-w-2xl font-heading text-4xl font-extrabold text-white sm:text-5xl">{L("Więcej kwiatów. Więcej zapylaczy. Więcej polskiego plonu.", "More flowers. More pollinators. More Polish harvest.")}</h1>
          <p className="mt-5 max-w-xl text-lg text-white/85">{L("Zapylacze są infrastrukturą rolnictwa. Pszczelarze są producentami żywności. Pochodzenie miodu musi być prawdziwe i identyfikowalne.", "Pollinators are agricultural infrastructure. Beekeepers are food producers. Honey origin must be truthful and traceable.")}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => document.getElementById("kw-form").scrollIntoView({ behavior: "smooth" })} className="rounded-md bg-white px-5 py-3 text-sm font-semibold text-forest" style={{ background: "#E1B84B" }} data-testid="kw-cta">{L("Dołącz jako pszczelarz", "Join as a beekeeper")}</button>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16">
        <SectionHeading eyebrow="Polska Kwitnie" title={L("Filary programu", "Program pillars")} />
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map(({ icon: Icon, t, d }) => (
            <div key={t} className="rounded-md border border-subtle bg-white p-5">
              <span className="grid h-11 w-11 place-items-center rounded-md" style={{ background: "#F6EAD0" }}><Icon className="h-5 w-5" style={{ color: "#C98A1D" }} aria-hidden="true" /></span>
              <div className="mt-3 font-heading font-bold text-charcoal">{t}</div>
              <p className="mt-1 text-sm text-mutedtext">{d}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 text-center">
          <Link to="/konrad-kwiatkowski" data-testid="kwitnie-founder-link" className="text-sm font-semibold text-forest hover:underline">
            {L("Założyciel — Konrad Kwiatkowski", "Founder — Konrad Kwiatkowski")}
          </Link>
        </div>
      </section>

      <section className="border-y border-subtle" style={{ background: "#F6EAD0" }}>
        <div className="mx-auto max-w-4xl px-4 py-8 text-center">
          <p className="text-sm font-semibold" style={{ color: "#8A5A12" }}>{L("Miodu nie wolno podawać dzieciom poniżej 12. miesiąca życia.", "Honey must not be given to children under 12 months.")}</p>
        </div>
      </section>

      {/* Honey traceability lookup */}
      <section className="mx-auto max-w-3xl px-4 py-14">
        <SectionHeading eyebrow={L("Identyfikowalność", "Traceability")} title={L("Sprawdź pochodzenie miodu", "Check honey origin")} />
        <div className="mt-5 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-mutedtext" aria-hidden="true" />
            <input value={batch} onChange={(e) => setBatch(e.target.value)} placeholder="MZP-DEMO03" data-testid="honey-batch-input"
              className="w-full rounded-md border border-input bg-white py-2 pl-9 pr-3 text-sm focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/40" />
          </div>
          <Btn onClick={() => batch && nav(`/honey/${batch.trim()}`)} data-testid="honey-lookup-btn">{L("Sprawdź", "Look up")}</Btn>
        </div>
      </section>

      {/* Interest form */}
      <section id="kw-form" className="mx-auto max-w-2xl px-4 py-14">
        <SectionHeading eyebrow={L("Zgłoszenie", "Interest")} title={L("Dla pszczelarzy i partnerów", "For beekeepers & partners")} />
        {ref ? (
          <div className="mt-6 rounded-md border border-forest/20 bg-forest/5 p-6 text-center" data-testid="kw-success">
            <p className="font-semibold text-charcoal">{L("Dziękujemy!", "Thank you!")}</p>
            <p className="mt-1 text-sm text-mutedtext">{L("Numer", "Reference")}: <span className="font-mono">{ref}</span></p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="kw-form">
            <Field label={L("Imię i nazwisko / nazwa", "Name")} required><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="kw-name" /></Field>
            <Field label={L("Pasieka / organizacja", "Apiary / organization")}><Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} /></Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="E-mail"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label={L("Region", "Region")}><Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="Lubelskie" /></Field>
            </div>
            <Field label={L("Wiadomość", "Message")}><Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></Field>
            <Btn type="submit" data-testid="kw-submit">{L("Wyślij", "Submit")}</Btn>
          </form>
        )}
      </section>
    </div>
  );
}
